/*
 * Copyright (c) 2023-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

class ReconnectingWebSocket {
    constructor(url, options = {}) {
        this.url = url;

        // Options
        this.debug = options.debug ?? false;
        this.reconnectInterval = options.reconnectInterval ?? 1000;
        this.maxReconnectInterval = options.maxReconnectInterval ?? 10000;
        this.reconnectDecay = options.reconnectDecay ?? 1.5;
        this.timeoutInterval = options.timeoutInterval ?? 2000;
        this.maxReconnectAttempts = options.maxReconnectAttempts ?? null;
        this.binaryType = options.binaryType ?? 'blob';

        // State
        this.reconnectAttempts = 0;
        this.readyState = null;
        this.protocol = null;
        this._listeners = {open: [], close: [], message: [], error: []};
        this._ws = null;
        this._forcedClose = false;
        this._pingInterval = null;

        this.open(false);
    }

    async open(reconnectAttempt = false) {
        if (reconnectAttempt && this.maxReconnectAttempts &&
            this.reconnectAttempts > this.maxReconnectAttempts) {
            return;
        }

        if (!reconnectAttempt) this.reconnectAttempts = 0;

        this._log('attempt-connect');

        this._ws = new WebSocket(this.url);
        this._ws.binaryType = this.binaryType;

        // Clear any existing timeout
        if (this._connectTimeout) {
            clearTimeout(this._connectTimeout);
        }

        this._connectTimeout = setTimeout(() => {
            this._log('connection-timeout');
            if (this._ws) {
                this._ws.close();
            }
        }, this.timeoutInterval);

        this._ws.onopen = () => {
            if (this._connectTimeout) {
                clearTimeout(this._connectTimeout);
                this._connectTimeout = null;
            }
            this._log('onopen');
            this.protocol = this._ws.protocol;
            this.readyState = WebSocket.OPEN;
            this.reconnectAttempts = 0;

            this._startPing();

            this._emit('open', {isReconnect: reconnectAttempt});
        };

        this._ws.onclose = (event) => {
            this._stopPing();

            if (this._connectTimeout) {
                clearTimeout(this._connectTimeout);
                this._connectTimeout = null;
            }

            // Clean up event listeners
            this._ws.onopen = null;
            this._ws.onclose = null;
            this._ws.onmessage = null;
            this._ws.onerror = null;

            this._ws = null;

            if (this._forcedClose) {
                this.readyState = WebSocket.CLOSED;
                this._emit('close', event);
                // Resolve the close promise if it exists
                if (this._closePromise) {
                    this._closePromise.resolve(event);
                    this._closePromise = null;
                }
            } else {
                this._log('onclose');
                this.readyState = WebSocket.CONNECTING;

                const delay = Math.min(
                    this.reconnectInterval * Math.pow(this.reconnectDecay, this.reconnectAttempts),
                    this.maxReconnectInterval
                );

                setTimeout(() => {
                    this.reconnectAttempts++;
                    this.open(true);
                }, delay);
            }
        };

        this._ws.onmessage = (event) => {
            this._log('onmessage', event.data);
            this._emit('message', event.data);
        };

        this._ws.onerror = (event) => {
            this._log('onerror', event);
            this._emit('error', event);
        };
    }

    _startPing() {
        this._stopPing();
        // ping the server every 30 s to tell the proxy to keep the connection open
        this._pingInterval = setInterval(() => {
            if (this._ws && this._ws.readyState === WebSocket.OPEN) {
                this.send(JSON.stringify(["ping", {}]));
            }
        }, 30000); // 30 seconds
    }

    _stopPing() {
        if (this._pingInterval) {
            clearInterval(this._pingInterval);
            this._pingInterval = null;
        }
    }

    on(event, handler) {
        if (this._listeners[event]) {
            this._listeners[event].push(handler);
        }
    }

    off(event, handler) {
        if (this._listeners[event]) {
            const index = this._listeners[event].indexOf(handler);
            if (index !== -1) {
                this._listeners[event].splice(index, 1);
            }
        }
    }

    send(data, retryCount = 0) {
        if (this._ws?.readyState === WebSocket.OPEN) {
            this._log('send', data);
            return this._ws.send(data);
        } else if (retryCount < 20) {
            setTimeout(() => this.send(data, retryCount + 1), 600);
        }
    }

    close(code = 1000, reason) {
        this._forcedClose = true;
        this._stopPing();
        this._ws?.close(code, reason);
    }

    refresh() {
        this._ws?.close();
    }

    _emit(event, data) {
        this._listeners[event]?.forEach(fn => fn(data));
    }

    _log(action, ...args) {
        if (this.debug) {
            console.debug('ReconnectingWebSocket', action, this.url, ...args);
        }
    }
}

export {ReconnectingWebSocket};
